import { RegisterComponent } from '@/common/register-component';
import { debug, isSameDayTimezone } from '@/utils/utils';
import { Component, Element, Event, EventEmitter, Host, Method, Prop, State, Watch, h } from '@stencil/core';
import { NylasSchedulerConnector } from '../../..';
import { NylasScheduling } from '../nylas-scheduling/nylas-scheduling';
import { AvailabilityTimeslot } from '@/stores/scheduler-store';
import type { ThemeConfig, Timeslot } from '@nylas/core';
import i18next from '@/utils/i18n';
import { LANGUAGE_CODE, LANGUAGE_CODE_TO_LOCALE_MAP } from '@/common/constants';

/**
 * The `nylas-timeslot-picker` component is a UI component that allows users to select a timeslot from a list of available timeslots.
 *
 * @slot timeslot-picker-cta-label - The label for the timeslot picker CTA. Default is "Next".
 * @part ntp - The timeslot picker component.
 * @part ntp__timeslot - The timeslot button.
 * @part ntp__timeslot--selected - The selected timeslot button.
 * @part ntp__button-primary - The timeslot picker CTA.
 * @part ntp__time-picker-wrapper - The timeslot picker wrapper.
 * @part ntp__timeslots - The timeslot picker timeslots.
 */
@Component({
  tag: 'nylas-timeslot-picker',
  styleUrl: 'nylas-timeslot-picker.scss',
  shadow: true,
})
export class NylasTimeslotPicker {
  /**
   * The host element.
   * Used to manage the host element of the provider.
   */
  @Element() private host!: HTMLNylasTimeslotPickerElement;
  /**
   * @standalone
   * The available timeslots.
   */
  @Prop({ attribute: 'availability' }) readonly availability?: AvailabilityTimeslot[];
  /**
   * @standalone
   * The loading state prop. Used to display loading state when fetching availability.
   */
  @Prop({ attribute: 'loading-state' }) readonly isLoading?: boolean;
  /**
   * @standalone
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: any;
  /**
   * The selected timeslot.
   */
  @Prop({ attribute: 'selected-timeslot' }) readonly selectedTimeslot?: Timeslot;
  /**
   * The selected timezone.
   */
  @Prop({ attribute: 'selected-timezone' }) readonly selectedTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
  /**
   * The selected date.
   */
  @Prop({ attribute: 'selected-date' }) readonly selectedDate?: Date = new Date();
  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: string;
  /**
   * The 12-hour time format.
   */
  @Prop() hour12?: boolean;

  /**
   * This event is fired when a timeslot is selected.
   */
  @Event() readonly timeslotSelected!: EventEmitter<Timeslot>;
  /**
   * This event is fired when a timeslot is confirmed. By default, this will proceed to the booking form page.
   */
  @Event() readonly timeslotConfirmed!: EventEmitter<{ timeslot: Timeslot; host: HTMLNylasTimeslotPickerElement }>;

  @Event() readonly validateTimeslotError!: EventEmitter<{ error: string; type: 'error' | 'info' }>;

  @State() times: AvailabilityTimeslot[] = [];
  @State() hoveredTimeslotIndex?: number;
  @State() selectedTimeslotIndex?: string;

  @Watch('selectedLanguage')
  selectedLanguageChanged(newLanguage: LANGUAGE_CODE) {
    i18next.changeLanguage(newLanguage);
  }

  @Watch('selectedDate')
  selectedDateChanged(newVal: Date | null | undefined) {
    debug(`[nylas-timeslot-picker] Selected date changed`, { newVal });

    if (newVal) {
      this.selectedTimeslotIndex = '';
      if (typeof newVal !== 'undefined') {
        const availableTimes = this.availability?.filter(timeslot => isSameDayTimezone(timeslot.start_time, newVal, this.selectedTimezone)).map(timeslot => timeslot);
        this.times = availableTimes || [];
      }
    }
  }

  @Watch('availability')
  availabilityChanged(newVal: AvailabilityTimeslot[] | undefined) {
    debug(`[nylas-timeslot-picker] Available times changed`, { newVal, selectedDate: this.selectedDate });
    if (typeof newVal !== 'undefined') {
      const availableTimes = newVal
        ?.filter(timeslot => this.selectedDate && isSameDayTimezone(timeslot.start_time, this.selectedDate, this.selectedTimezone))
        .map(timeslot => timeslot);
      this.times = availableTimes || [];
    }
  }

  @Watch('selectedTimezone')
  selectedTimezoneChanged(newTimezone: string) {
    debug(`[nylas-timeslot-picker] Selected timezone changed`, { newTimezone, selectedDate: this.selectedDate });
    // Recalculate times when timezone changes because a timeslot that was on
    // "January 7th in UTC" might be on "January 8th in Tokyo timezone"
    if (this.selectedDate && this.availability) {
      const availableTimes = this.availability?.filter(timeslot => isSameDayTimezone(timeslot.start_time, this.selectedDate!, newTimezone)).map(timeslot => timeslot);
      this.times = availableTimes || [];
    }
  }

  @Watch('themeConfig')
  themeConfigChanged(newThemeConfig: ThemeConfig) {
    this.applyThemeConfig(newThemeConfig);
  }

  connectedCallback() {
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  disconnectedCallback() {}

  async componentWillLoad() {
    debug(`[nylas-timeslot-picker] Component will load`, { selectedDate: this.selectedDate, availability: this.availability, times: this.times });
  }

  async componentDidLoad() {
    debug(`[nylas-timeslot-picker] Component did load`);
    const availableTimes = this.availability
      ?.filter(timeslot => this.selectedDate && isSameDayTimezone(timeslot.start_time, this.selectedDate, this.selectedTimezone))
      .map(timeslot => timeslot);
    this.times = availableTimes || [];
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  private getTimeslotId(timeslot: Timeslot, index: number) {
    const date = new Date(timeslot.start_time);
    const eventId = timeslot.event_id;
    return `${date.toLocaleDateString()}-${index}${eventId ? `-${eventId}` : ''}`;
  }

  private onClickSelectTime(timeslot: Timeslot, index: number) {
    debug(`[nylas-timeslot-picker] Time selected`, timeslot, index);
    this.selectedTimeslotIndex = this.getTimeslotId(timeslot, index);
    this.timeslotSelected.emit({
      start_time: timeslot.start_time,
      end_time: timeslot.end_time,
      emails: timeslot.emails,
      capacity: timeslot?.capacity,
      event_id: timeslot?.event_id,
      master_id: timeslot?.master_id,
      calendar_id: timeslot?.calendar_id,
    });
  }

  private handleConfirmedTimeslot = async (event: Event, timeslot: Timeslot | undefined) => {
    event.preventDefault();
    debug(`[nylas-timeslot-picker] Confirm timeslot`, timeslot);
    if (timeslot) {
      this.timeslotConfirmed.emit({ timeslot, host: this.host });
    }
  };

  @Method()
  async emitValidateTimeslotError(error: string, type: 'error' | 'info') {
    this.validateTimeslotError.emit({ error, type });
  }

  private handleMouseEnter = (index: number) => {
    this.hoveredTimeslotIndex = index;
  };

  private handleMouseLeave = () => {
    this.hoveredTimeslotIndex = -1;
  };

  private getTimeSlotLabel(timeslot: Timeslot) {
    const locale = LANGUAGE_CODE_TO_LOCALE_MAP[this.selectedLanguage || 'en'];
    const timeFormat = new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: this.selectedTimezone,
      hour12: this.hour12,
    });
    return `${timeFormat.format(timeslot.start_time)} - ${timeFormat.format(timeslot.end_time)}`;
  }

  @RegisterComponent<NylasTimeslotPicker, NylasSchedulerConnector, Exclude<NylasScheduling['stores'], undefined>>({
    name: 'nylas-timeslot-picker',
    stateToProps: new Map([
      ['scheduler.selectedDate', 'selectedDate'],
      ['scheduler.availability', 'availability'],
      ['scheduler.selectedTimeslot', 'selectedTimeslot'],
      ['scheduler.selectedTimezone', 'selectedTimezone'],
      ['scheduler.selectedLanguage', 'selectedLanguage'],
      ['scheduler.isLoading', 'isLoading'],
      ['scheduler.themeConfig', 'themeConfig'],
      ['scheduler.hour12', 'hour12'],
    ]),
    eventToProps: {
      timeslotSelected: async (event: CustomEvent<Timeslot>, nylasSchedulerConnector: NylasSchedulerConnector) => {
        debug('nylas-timeslot-picker', 'timeslotSelected', event.detail);
        nylasSchedulerConnector.scheduler.selectTime(event.detail);
      },
      timeslotConfirmed: async (event: CustomEvent<{ timeslot: Timeslot; host: HTMLNylasTimeslotPickerElement }>, nylasSchedulerConnector: NylasSchedulerConnector) => {
        debug('nylas-timeslot-picker', 'timeslotConfirmed', event.detail);
        const { timeslot, host } = event.detail;
        if (timeslot && timeslot?.event_id && timeslot?.capacity) {
          const result = await nylasSchedulerConnector.scheduler.validateGroupEventTimeslot(timeslot);
          if (result && 'error' in result) {
            const errorMessage = result.error?.message || 'An error occurred while validating the timeslot';
            if (errorMessage === 'Event is full') {
              host.emitValidateTimeslotError(errorMessage, 'info');
            } else {
              host.emitValidateTimeslotError(errorMessage, 'error');
            }
          }
        } else {
          nylasSchedulerConnector.scheduler.toggleAdditionalData(true);
        }
      },
      validateTimeslotError: async (event: CustomEvent<{ error: string }>, _nylasSchedulerConnector: NylasSchedulerConnector) => {
        debug('nylas-timeslot-picker', 'validateTimeslotError', event.detail);
      },
    },
    fireRegisterEvent: true,
  })
  render() {
    const locale = LANGUAGE_CODE_TO_LOCALE_MAP[this.selectedLanguage || 'en'];
    if (this.isLoading) {
      return (
        <Host>
          <div class={'loading'}>
            {new Array(6).fill(0).map((_, i) => (
              <div class={'timeslot-skeleton'} style={{ animationDelay: `${i * 20}ms` }} />
            ))}
          </div>
        </Host>
      );
    }

    if (this.availability && this.availability.length === 0) {
      return (
        <Host>
          <div class={'empty'}>
            <span>No dates available</span>
          </div>
        </Host>
      );
    }

    const filteredTimes = this.times.filter(timeslot => {
      const groupEvent = timeslot?.event_id || timeslot?.master_id;
      const showCapacity = typeof timeslot?.capacity === 'number' && timeslot?.capacity > 0;
      return !(groupEvent && !showCapacity);
    });

    if ((!this.times || this.times.length === 0 || filteredTimes.length === 0) && !!this.selectedDate) {
      return (
        <Host>
          <div class={'empty'}>
            <span>No time slots available for selected date</span>
          </div>
        </Host>
      );
    }

    if (!this.selectedDate) return <Host></Host>;

    return (
      <Host part="ntp">
        <div class={'time-picker-wrapper'} part="ntp__time-picker-wrapper">
          <div class={'timeslots'} part="ntp__timeslots">
            {filteredTimes.map((timeslot, index) => {
              const showCapacity = typeof timeslot?.capacity === 'number' && timeslot?.capacity > 0;
              const isSelected = this.selectedTimeslotIndex == this.getTimeslotId(timeslot, index);
              return (
                <button
                  part={`ntp__timeslot ${this.selectedTimeslot?.start_time?.getTime() === timeslot.start_time.getTime() ? 'ntp__timeslot--selected' : ''}`}
                  class={{
                    time: true,
                    selected: typeof this.selectedTimeslot !== 'undefined' && isSelected,
                  }}
                  onClick={() => this.onClickSelectTime(timeslot, index)}
                  onMouseEnter={() => this.handleMouseEnter(index)}
                  onMouseLeave={() => this.handleMouseLeave()}
                >
                  <div class="time-label" part="ntp__time-label">
                    {this.hoveredTimeslotIndex == index || isSelected
                      ? this.getTimeSlotLabel(timeslot)
                      : timeslot.start_time.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone: this.selectedTimezone, hour12: this.hour12 })}
                    <span class="spots">{showCapacity ? i18next.t('spotsLeft', { count: timeslot?.capacity }) : null}</span>
                  </div>
                </button>
              );
            })}
          </div>
          {this.selectedTimeslot && (
            <div class={'footer'}>
              <button-component variant={'primary'} onClick={(event: Event) => this.handleConfirmedTimeslot(event, this.selectedTimeslot)} part="ntp__button-primary">
                <slot name="timeslot-picker-cta-label">{i18next.t('nextButton')}</slot>
              </button-component>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
